import { useMsal, useAccount } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useCallback } from "react";

/**
 * Custom auth hook that bridges MSAL with ConvexProviderWithAuth.
 * Returns idToken (NOT accessToken) because Convex validates ID tokens
 * issued by the Entra ID tenant.
 */
export function useConvexMsalAuth() {
  const { instance, inProgress, accounts } = useMsal();
  const account = useAccount(accounts[0] ?? null);

  const fetchAccessToken = useCallback(
    async ({ forceRefreshToken }: { forceRefreshToken: boolean }) => {
      if (!account) return null;
      try {
        const response = await instance.acquireTokenSilent({
          scopes: ["openid", "profile", "email"],
          account,
          forceRefresh: forceRefreshToken,
        });
        return response.idToken;
      } catch (err) {
        // IMPORTANT: do NOT fall back to acquireTokenPopup here.
        //
        // Convex's ConvexProviderWithAuth calls fetchAccessToken whenever it
        // refreshes the auth token (and sometimes with forceRefreshToken=true).
        // Any transient silent failure — network blip, MSAL cache miss,
        // tenant policy hiccup — used to spawn a Microsoft popup every time,
        // which on a long-running dashboard turned into "the app keeps asking
        // me to re-authenticate constantly."
        //
        // The App.tsx auth gate already covers genuine session loss:
        // useIsAuthenticated() flips to false once MSAL drops the account, and
        // the user is shown the LoginPage to sign in once, cleanly. So here
        // we just return null on failure — Convex treats this frame as
        // unauthenticated and retries on the next tick; if it's transient the
        // next acquireTokenSilent succeeds and the user never notices.
        console.warn("[auth] acquireTokenSilent failed; not prompting popup", err);
        return null;
      }
    },
    [account, instance],
  );

  return {
    isLoading: inProgress !== InteractionStatus.None,
    isAuthenticated: !!account,
    fetchAccessToken,
  };
}
